import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, Copy, GitPullRequest } from 'lucide-react'
import { Button, DetailSection, FormError, LoadFailure, Skeleton } from '../../../core/index.js'
import type { FeedbackAdminApi } from '../../adminApi.js'
import type { ChangeRequestView } from '../../types.js'
import { isPullRequestUrl, isSha } from './changeText.js'
import { changeQueryKey, useChangeStep } from './useChangeStep.js'

const COPIED_FOR_MS = 2000
const INPUT_CLASS = 'mt-1 w-full border border-marine/30 rounded px-2 py-1 text-xs font-normal normal-case tracking-normal text-ink bg-white focus:outline-none focus:ring-2 focus:ring-coral'

/** The approved version's prompt, for the root to paste into Claude Code (phase 0 has no dispatch). */
export function PromptPanel({ api, changeRequestId }: { api: Pick<FeedbackAdminApi, 'changePrompt'>; changeRequestId: string }) {
  const promptQuery = useQuery({ queryKey: [...changeQueryKey(changeRequestId), 'prompt'], queryFn: () => api.changePrompt(changeRequestId) })
  const [copied, setCopied] = useState<'yes' | 'failed' | null>(null)

  async function copy(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text)
      setCopied('yes')
    } catch {
      setCopied('failed')
    }
    window.setTimeout(() => setCopied(null), COPIED_FOR_MS)
  }

  return (
    <DetailSection title="The approved prompt" endpoint="GET /admin/changes/{changeRequestId}/prompt">
      {promptQuery.isPending ? (
        <Skeleton label="Loading the prompt" />
      ) : promptQuery.isError ? (
        <LoadFailure size="xs" message="Could not load the prompt." onRetry={() => void promptQuery.refetch()} retrying={promptQuery.isFetching} />
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-ink-mid">
            <span>
              Branch <span className="font-mono text-ink select-all">{promptQuery.data.branch}</span>
            </span>
            <span>Version {promptQuery.data.requirementVersion}</span>
            <Button icon={copied === 'yes' ? Check : Copy} size="sm" className="ml-auto" onClick={() => void copy(promptQuery.data.prompt)}>
              {copied === 'yes' ? 'Copied' : 'Copy prompt'}
            </Button>
          </div>
          {copied === 'failed' && <p className="text-[11px] font-bold text-coral">The browser would not copy; select the text below instead.</p>}
          <textarea readOnly value={promptQuery.data.prompt} rows={14} className="w-full border border-marine/20 rounded bg-canvas p-2 font-mono text-[11px] text-ink" />
        </div>
      )}
    </DetailSection>
  )
}

/** Records the pull request that came back, at the commit to review; recording again replaces it (a new push). */
export function PullRequestForm({ api, change }: { api: Pick<FeedbackAdminApi, 'recordPullRequest'>; change: ChangeRequestView }) {
  const [url, setUrl] = useState(change.pullRequest?.url ?? '')
  const [headSha, setHeadSha] = useState('')
  const [branch, setBranch] = useState(change.pullRequest?.branch ?? '')
  const step = useChangeStep(() => setHeadSha(''))
  const urlValid = isPullRequestUrl(url)
  const shaValid = isSha(headSha)

  return (
    <DetailSection title={change.pullRequest ? 'Record a newer commit' : 'Record the pull request'} endpoint="POST /admin/changes/{changeRequestId}/pull-request">
      <div className="grid gap-2 sm:grid-cols-3">
        <label className="block text-[10px] font-bold uppercase tracking-wide text-ink-mid sm:col-span-3">
          Pull request URL
          <input value={url} onChange={(event) => setUrl(event.target.value)} placeholder="https://github.com/<owner>/<repo>/pull/<number>" className={INPUT_CLASS} />
        </label>
        <label className="block text-[10px] font-bold uppercase tracking-wide text-ink-mid">
          Head commit SHA
          <input value={headSha} onChange={(event) => setHeadSha(event.target.value)} className={`${INPUT_CLASS} font-mono`} />
        </label>
        <label className="block text-[10px] font-bold uppercase tracking-wide text-ink-mid sm:col-span-2">
          Branch <span className="font-normal normal-case text-ink-mid/70">Optional</span>
          <input value={branch} onChange={(event) => setBranch(event.target.value)} className={`${INPUT_CLASS} font-mono`} />
        </label>
      </div>
      {url.trim() !== '' && !urlValid && <p className="mt-1 text-[11px] font-bold text-coral">A GitHub pull request address, ending /pull/ and its number.</p>}
      {headSha.trim() !== '' && !shaValid && <p className="mt-1 text-[11px] font-bold text-coral">A commit SHA is 7 to 40 hexadecimal characters.</p>}
      <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
        <div className="mr-auto">
          <FormError error={step.error} fallback="Could not record the pull request." />
        </div>
        <Button
          icon={GitPullRequest}
          size="sm"
          disabled={!urlValid || !shaValid || step.isPending}
          onClick={() => step.mutate(() => api.recordPullRequest(change.changeRequestId, { url: url.trim(), headSha: headSha.trim(), ...(branch.trim() ? { branch: branch.trim() } : {}) }))}
        >
          {step.isPending ? 'Recording…' : 'Record pull request'}
        </Button>
      </div>
    </DetailSection>
  )
}
